import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '../components/ui/resizable';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';
import api from '../lib/api';
import { getAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, FileText, Loader2, Save, Edit, X, Plus, RefreshCw, ZoomIn, ZoomOut, Check, AlertTriangle, AlertCircle, History, UserCheck, Lock, Download, ChevronDown, RotateCw, RotateCcw, ChevronRight } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { format } from 'date-fns';

// Use CDN worker that matches the bundled pdfjs version
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Confidence indicator component
const ConfidenceIndicator = ({ confidence, field }) => {
  if (!confidence) return null;
  
  const { level, reason } = confidence;
  
  const icons = {
    high: <Check className="h-4 w-4 text-green-600" />,
    medium: <AlertTriangle className="h-4 w-4 text-yellow-600" />,
    low: <AlertCircle className="h-4 w-4 text-red-600" />
  };
  
  const colors = {
    high: 'border-green-200 bg-green-50',
    medium: 'border-yellow-200 bg-yellow-50',
    low: 'border-red-200 bg-red-50'
  };
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={`inline-flex items-center justify-center w-6 h-6 rounded ${colors[level]} border cursor-help`}>
            {icons[level]}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <p className="text-xs font-medium capitalize">{level} Confidence</p>
          <p className="text-xs text-muted-foreground">{reason}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Usage Cost Breakdown Accordion Component
const UsageCostBreakdown = ({ usageBreakdown, otherCharges, validationData }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  // Combine items from _usage_breakdown
  const allBreakdownItems = useMemo(() => {
    const items = [];
    
    // Add pre-segregated energy charges from _usage_breakdown
    if (usageBreakdown && usageBreakdown.length > 0) {
      usageBreakdown.forEach(item => {
        items.push({
          description: item.description || item.line_item_description || 'Charge',
          charge: parseFloat(item.amount || item.charge || 0),
          category: item.category || 'Energy Charges'
        });
      });
    }
    
    return items;
  }, [usageBreakdown]);
  
  // Group by category
  const groupedItems = useMemo(() => {
    const groups = {};
    allBreakdownItems.forEach(item => {
      const category = item.category || 'Other';
      if (!groups[category]) {
        groups[category] = { items: [], total: 0 };
      }
      groups[category].items.push(item);
      groups[category].total += item.charge;
    });
    return groups;
  }, [allBreakdownItems]);
  
  const totalBreakdown = allBreakdownItems.reduce((sum, item) => sum + item.charge, 0);
  
  if (allBreakdownItems.length === 0) return null;
  
  return (
    <div className="border border-border border-t-0">
      {/* Collapsed Header */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 transition-colors"
        data-testid="usage-cost-breakdown-toggle"
      >
        <div className="flex items-center gap-2">
          <ChevronRight className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
          <span className="text-sm font-medium">Usage Cost Breakdown</span>
          <Badge variant="secondary" className="rounded-none text-xs">
            {allBreakdownItems.length} items
          </Badge>
        </div>
        <span className="text-sm font-mono font-medium">${totalBreakdown.toFixed(2)}</span>
      </button>
      
      {/* Expanded Content */}
      {isOpen && (
        <div className="p-4 bg-white space-y-4" data-testid="usage-cost-breakdown-content">
          {Object.entries(groupedItems).map(([category, group]) => (
            <div key={category} className="space-y-2">
              <div className="flex items-center justify-between text-sm font-semibold text-slate-700 border-b pb-1">
                <span>{category}</span>
                <span className="font-mono">${group.total.toFixed(2)}</span>
              </div>
              <div className="space-y-1 pl-4">
                {group.items.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between text-xs text-slate-600">
                    <span className="truncate max-w-[280px]" title={item.description}>
                      {item.description}
                    </span>
                    <span className="font-mono">${item.charge.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
          
          {/* Validation Fixes Applied */}
          {validationData?.fixes_applied && validationData.fixes_applied.length > 0 && (
            <div className="border-t pt-3 mt-3">
              <p className="text-xs font-medium text-slate-600 mb-2">Auto-corrections Applied:</p>
              <div className="space-y-1">
                {validationData.fixes_applied.map((fix, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-xs text-green-700">
                    <Check className="h-3 w-3" />
                    <span>{fix}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Summary Footer */}
          <div className="border-t pt-2 mt-3">
            <div className="flex items-center justify-between text-sm font-bold">
              <span>Total Breakdown</span>
              <span className="font-mono">${totalBreakdown.toFixed(2)}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              These energy charges were segregated from other charges for validation.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export const Viewer = () => {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const currentAccountStr = localStorage.getItem('current_account');
  const currentAccount = currentAccountStr ? JSON.parse(currentAccountStr) : null;
  
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [numPages, setNumPages] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reparsing, setReparsing] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoadError, setPdfLoadError] = useState(null);
  const [hasJpeg2000Warning, setHasJpeg2000Warning] = useState(false);
  
  // PDF Zoom, Search, Navigation and Rotation
  const [scale, setScale] = useState(1.0);
  const [rotation, setRotation] = useState(0);  // NEW: PDF rotation (0, 90, 180, 270)
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const pdfContainerRef = useRef(null);
  
  // Confidence scores
  const [confidenceScores, setConfidenceScores] = useState({});
  
  // Document workflow
  const [reviewStatus, setReviewStatus] = useState('review');
  const [assignedTo, setAssignedTo] = useState(null);
  const [accountUsers, setAccountUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [showAuditModal, setShowAuditModal] = useState(false);
  
  // BillPortal export
  const [hasBillPortalExport, setHasBillPortalExport] = useState(false);
  
  // Parser config (fetched to check enable_intelligent_validation)
  const [parserConfig, setParserConfig] = useState(null);
  
  // Row editing state
  const [editingRowId, setEditingRowId] = useState(null); // e.g., 'meter-0', 'usage-2'
  const [reviewedRows, setReviewedRows] = useState(new Set()); // Rows marked as worked/checked
  const [showValidationWarnings, setShowValidationWarnings] = useState(false);
  
  // Statement data - Unified schema for both single and consolidated bills
  const [statement, setStatement] = useState({
    // Property/Location Info
    property_name: '',
    service_address: '',
    city: '',
    state: '',
    postcode: '',
    // Customer Info
    customer_name: '',
    account_no: '',
    meter_number: '',  // NEW: Primary meter number
    // Vendor Info
    vendor_name: '',
    commodity: 'Electricity',
    // Invoice Info
    invoice_no: '',
    issue_date: '',
    due_date: '',
    period_from: '',
    period_to: '',
    // Financial Info
    previous_balance: '',
    payments_received: '',
    balance_forward: '',
    late_fee: '',      // NEW: Late fee at statement level
    credits: '',       // NEW: Credits applied
    adjustments: '',   // NEW: Adjustments
    new_charges: '',
    total_amount_due: ''
  });
  
  // NEW: Validation state from AI extraction
  const [validationData, setValidationData] = useState(null);
  
  const [providerCharges, setProviderCharges] = useState([]);
  const [meterReads, setMeterReads] = useState([]);
  const [usageDetails, setUsageDetails] = useState([]);
  const [otherCharges, setOtherCharges] = useState([]);
  const [usageBreakdown, setUsageBreakdown] = useState([]); // NEW: Energy charges breakdown

  // Balance validation logic - Updated per user requirements
  const balanceValidation = useMemo(() => {
    const parseAmount = (val) => {
      if (!val || val === '' || val === 'None' || val === 'N/A') return 0;
      const cleaned = String(val).replace(/[$,]/g, '').trim();
      return parseFloat(cleaned) || 0;
    };

    const previousBalance = parseAmount(statement.previous_balance);
    const paymentsReceived = parseAmount(statement.payments_received);
    const balanceForward = parseAmount(statement.balance_forward);
    const newCharges = parseAmount(statement.new_charges);
    const totalAmountDue = parseAmount(statement.total_amount_due);

    // Updated BBF Logic:
    // If Previous Balance = Payments Received, then BBF can be zero (valid)
    // If Previous Balance != Payments Received, BBF = Previous Balance - Payments Received
    const expectedBalanceForward = previousBalance - paymentsReceived;
    
    let isValid = false;
    let message = '';

    // Rule 1: No previous balance data at all - skip validation
    if (previousBalance === 0 && paymentsReceived === 0 && balanceForward === 0) {
      isValid = true;
      message = 'No outstanding balance';
    }
    // Rule 2: Previous Balance equals Payments Received - BBF should be 0
    else if (Math.abs(previousBalance - paymentsReceived) < 0.01) {
      if (balanceForward === 0) {
        isValid = true;
        message = 'Previous balance paid in full (BBF = 0)';
      } else {
        isValid = false;
        message = `BBF should be 0 when Previous Balance = Payments Received`;
      }
    }
    // Rule 3: Previous Balance != Payments Received - BBF = Previous - Payments
    else if (Math.abs(expectedBalanceForward - balanceForward) < 0.01) {
      isValid = true;
      if (balanceForward > 0) {
        message = `BBF verified: $${balanceForward.toFixed(2)} (Previous - Payments)`;
      } else {
        message = 'Balance calculation verified';
      }
    }
    // Rule 4: If Balance Forward is 0 and New Charges equals Total Amount Due - valid scenario
    else if (balanceForward === 0 && newCharges > 0 && Math.abs(newCharges - totalAmountDue) < 0.01) {
      isValid = true;
      message = 'New charges equal total due (no carry-over balance)';
    }
    // Rule 5: Check if total adds up correctly
    else if (Math.abs((balanceForward + newCharges) - totalAmountDue) < 0.01) {
      isValid = true;
      message = 'Total amount due calculation verified';
    }
    else {
      isValid = false;
      message = `Expected BBF: $${expectedBalanceForward.toFixed(2)}, Actual: $${balanceForward.toFixed(2)}`;
    }

    return { isValid, message, previousBalance, paymentsReceived, balanceForward, expectedBalanceForward };
  }, [statement.previous_balance, statement.payments_received, statement.balance_forward, statement.new_charges, statement.total_amount_due]);

  // Meter read date validation
  const meterValidation = useMemo(() => {
    const validations = meterReads.map((read, index) => {
      const prevDate = read.previous_read_date;
      const currDate = read.current_read_date;
      
      if (!prevDate || !currDate) {
        return { index, isValid: true, message: '' }; // Skip if dates missing
      }
      
      const prevDateObj = new Date(prevDate);
      const currDateObj = new Date(currDate);
      
      if (isNaN(prevDateObj.getTime()) || isNaN(currDateObj.getTime())) {
        return { index, isValid: true, message: '' }; // Skip invalid dates
      }
      
      if (prevDateObj >= currDateObj) {
        return {
          index,
          isValid: false,
          message: `Meter ${read.meter_number || index + 1}: Previous read date (${prevDate}) must be before current read date (${currDate})`
        };
      }
      
      return { index, isValid: true, message: '' };
    });
    
    const hasErrors = validations.some(v => !v.isValid);
    const errorMessages = validations.filter(v => !v.isValid).map(v => v.message);
    
    return { validations, hasErrors, errorMessages };
  }, [meterReads]);

  // Usage line item validation (usage × rate = charge)
  const usageValidation = useMemo(() => {
    const parseNum = (val) => {
      if (!val || val === '' || val === 'None' || val === '-') return null;
      const cleaned = String(val).replace(/[$,]/g, '').trim();
      const num = parseFloat(cleaned);
      return isNaN(num) ? null : num;
    };

    const validations = usageDetails.map((item, index) => {
      const usage = parseNum(item.usage_quantity);
      const rate = parseNum(item.rate);
      const charge = parseNum(item.charge);
      
      // Skip validation if any required field is missing
      if (usage === null || rate === null || charge === null) {
        return { index, isValid: true, message: '', calculated: null };
      }
      
      const calculated = usage * rate;
      const difference = Math.abs(calculated - charge);
      const tolerance = Math.max(0.02, Math.abs(charge * 0.01)); // 1% or $0.02 tolerance
      
      if (difference > tolerance) {
        return {
          index,
          isValid: false,
          message: `Row ${index + 1}: ${usage} × ${rate} = ${calculated.toFixed(2)}, but charge shows ${charge.toFixed(2)}`,
          calculated
        };
      }
      
      return { index, isValid: true, message: '', calculated };
    });
    
    const hasErrors = validations.some(v => !v.isValid);
    const errorMessages = validations.filter(v => !v.isValid).map(v => v.message);
    
    return { validations, hasErrors, errorMessages };
  }, [usageDetails]);

  // UOM validation based on commodity
  const uomValidation = useMemo(() => {
    const expectedUOM = {
      'Electricity': ['kWh', 'kwh', 'KWH', 'MWh', 'mwh'],
      'Gas': ['Therms', 'therms', 'THERMS', 'CCF', 'ccf', 'MCF', 'Dth', 'DTH'],
      'Water': ['Gallons', 'gallons', 'GAL', 'gal', 'HCF', 'hcf', 'CCF', 'ccf', 'kGal', 'KGAL'],
      'Sewer': ['Gallons', 'gallons', 'GAL', 'gal', 'HCF', 'hcf', 'CCF', 'ccf']
    };

    const validations = usageDetails.map((item, index) => {
      const commodity = item.commodity || statement.commodity;
      const uom = item.uom;
      
      if (!commodity || !uom) {
        return { index, isValid: true, message: '' };
      }
      
      const expected = expectedUOM[commodity] || [];
      if (expected.length === 0) {
        return { index, isValid: true, message: '' };
      }
      
      const isValidUOM = expected.some(e => uom.toLowerCase().includes(e.toLowerCase()));
      
      if (!isValidUOM) {
        return {
          index,
          isValid: false,
          message: `Row ${index + 1}: UOM "${uom}" unexpected for ${commodity}. Expected: ${expected.slice(0, 3).join(', ')}`
        };
      }
      
      return { index, isValid: true, message: '' };
    });
    
    const hasErrors = validations.some(v => !v.isValid);
    const errorMessages = validations.filter(v => !v.isValid).map(v => v.message);
    
    return { validations, hasErrors, errorMessages };
  }, [usageDetails, statement.commodity]);

  // NEW: Comprehensive Bill Validation - Usage + Other Charges = New Charges, Total Amount Due verification
  const billValidation = useMemo(() => {
    const parseAmount = (val) => {
      if (!val || val === '' || val === 'None' || val === 'N/A') return 0;
      const cleaned = String(val).replace(/[$,]/g, '').trim();
      return parseFloat(cleaned) || 0;
    };

    // Calculate totals
    const usageTotal = usageDetails.reduce((sum, item) => sum + parseAmount(item.charges), 0);
    const otherChargesTotal = otherCharges.reduce((sum, item) => sum + parseAmount(item.charge), 0);
    const lineItemsSum = usageTotal + otherChargesTotal;
    
    // Statement values
    const newCharges = parseAmount(statement.new_charges);
    const totalAmountDue = parseAmount(statement.total_amount_due);
    const lateFee = parseAmount(statement.late_fee);
    const credits = parseAmount(statement.credits);
    const adjustments = parseAmount(statement.adjustments);
    const balanceForward = parseAmount(statement.balance_forward);
    
    // Validation checks
    const lineItemsMatchNewCharges = Math.abs(lineItemsSum - newCharges) < 0.01;
    
    // Total Due formula: New Charges + Late Fee - Credits + Adjustments + BBF
    const calculatedTotalDue = newCharges + lateFee - credits + adjustments + balanceForward;
    const totalDueMatches = Math.abs(calculatedTotalDue - totalAmountDue) < 0.01;
    
    // Calculate validation percentage
    let validationScore = 0;
    if (lineItemsMatchNewCharges) validationScore += 50;
    if (totalDueMatches) validationScore += 50;
    
    const isFullyValid = lineItemsMatchNewCharges && totalDueMatches;
    
    return {
      usageTotal,
      otherChargesTotal,
      lineItemsSum,
      newCharges,
      lineItemsMatchNewCharges,
      totalAmountDue,
      calculatedTotalDue,
      totalDueMatches,
      validationScore,
      isFullyValid,
      breakdown: {
        lateFee,
        credits,
        adjustments,
        balanceForward
      }
    };
  }, [usageDetails, otherCharges, statement.new_charges, statement.total_amount_due, statement.late_fee, statement.credits, statement.adjustments, statement.balance_forward]);

  // Intelligent Validation (Engine-2) - All line items from Usage + Other Charges
  const intelligentValidation = useMemo(() => {
    const parseAmount = (val) => {
      if (!val || val === '' || val === 'None' || val === 'N/A') return 0;
      const cleaned = String(val).replace(/[$,]/g, '').trim();
      return parseFloat(cleaned) || 0;
    };

    const newCharges = parseAmount(statement.new_charges);
    const totalAmountDue = parseAmount(statement.total_amount_due);
    
    // Collect ALL line items from both Usage Details and Other Charges
    const validationLineItems = [];
    
    // Add all usage details items
    usageDetails.forEach((item, idx) => {
      const charge = parseAmount(item.charges || item.charge);
      validationLineItems.push({
        ...item,
        source: 'Usage Details',
        originalIndex: idx,
        description: item.line_item_description || item.description || 'Usage Charge',
        charge,
        page_number: item.page_number
      });
    });
    
    // Add all other charges items
    otherCharges.forEach((item, idx) => {
      const charge = parseAmount(item.charge);
      validationLineItems.push({
        ...item,
        source: 'Other Charges',
        originalIndex: idx,
        description: item.line_item_description || 'Other Charge',
        charge,
        page_number: item.page_number
      });
    });
    
    // Calculate total of all line items
    const validationTotal = validationLineItems.reduce((sum, item) => sum + item.charge, 0);
    
    // NEW CHARGES VALIDATION: Line items should add up to New Charges
    const validationMatchesNewCharges = Math.abs(validationTotal - newCharges) <= 0.01;
    const newChargesDifference = validationTotal - newCharges;
    
    // TOTAL AMOUNT DUE VALIDATION: Multiple methods to check
    const lateFee = parseAmount(statement.late_fee);
    const credits = parseAmount(statement.credits);
    const adjustments = parseAmount(statement.adjustments);
    const balanceForward = parseAmount(statement.balance_forward);
    
    // Check if late fee is already included in Other Charges
    const lateFeeInOtherCharges = otherCharges.some(item => {
      const desc = (item.line_item_description || '').toLowerCase();
      return desc.includes('late') && Math.abs(parseAmount(item.charge) - lateFee) < 0.01;
    });
    
    // Method 1: Standard formula (New Charges + Balance Forward + Late Fee - Credits)
    // Only add late fee if it's NOT already in other charges
    const effectiveLateFee = lateFeeInOtherCharges ? 0 : lateFee;
    const calculatedTotalDue = newCharges + balanceForward + effectiveLateFee - credits + adjustments;
    const totalDueMatchesMethod1 = Math.abs(calculatedTotalDue - totalAmountDue) <= 0.01;
    
    // Method 2: If New Charges == Total Due (common for bills with no balance forward)
    const totalDueMatchesMethod2 = Math.abs(newCharges - totalAmountDue) <= 0.01;
    
    // Method 3: All Line Items + Balance Forward = Total Due (late fee already in line items)
    const allItemsPlusBalance = validationTotal + balanceForward - credits + adjustments;
    const totalDueMatchesMethod3 = Math.abs(allItemsPlusBalance - totalAmountDue) <= 0.01;
    
    // Method 4: New Charges + Balance Forward = Total Due (no late fee adjustment)
    const newChargesPlusBF = newCharges + balanceForward - credits + adjustments;
    const totalDueMatchesMethod4 = Math.abs(newChargesPlusBF - totalAmountDue) <= 0.01;
    
    // Total Due is valid if ANY method matches
    const totalDueMatches = totalDueMatchesMethod1 || totalDueMatchesMethod2 || totalDueMatchesMethod3 || totalDueMatchesMethod4;
    const totalDueDifference = totalDueMatchesMethod1 ? calculatedTotalDue - totalAmountDue :
                               totalDueMatchesMethod3 ? allItemsPlusBalance - totalAmountDue :
                               totalDueMatchesMethod4 ? newChargesPlusBF - totalAmountDue :
                               newCharges - totalAmountDue;
    
    // Determine validation method used for display
    const totalDueValidationMethod = totalDueMatchesMethod1 ? 'standard' :
                                     totalDueMatchesMethod2 ? 'direct_match' :
                                     totalDueMatchesMethod3 ? 'line_items_plus_balance' :
                                     totalDueMatchesMethod4 ? 'new_charges_plus_bf' : 'none';
    
    // BOTH VALID: When line items match New Charges AND Total Due can be validated
    const bothValid = validationMatchesNewCharges && totalDueMatches;
    
    // Calculate validation accuracy percentage (for consistency with bill list)
    let validationAccuracy = 0;
    if (validationMatchesNewCharges) validationAccuracy += 50;
    if (totalDueMatches) validationAccuracy += 50;
    
    return {
      enabled: parserConfig?.enable_intelligent_validation || false,
      validationLineItems,
      validationTotal,
      newCharges,
      validationMatchesNewCharges,
      newChargesDifference,
      totalAmountDue,
      calculatedTotalDue,
      totalDueMatches,
      totalDueDifference,
      totalDueValidationMethod,
      bothValid,
      validationAccuracy,
      breakdown: { 
        usageCount: usageDetails.length,
        otherCount: otherCharges.length,
        lateFee,
        effectiveLateFee,
        lateFeeInOtherCharges,
        credits, 
        adjustments, 
        balanceForward
      }
    };
  }, [usageDetails, otherCharges, statement, parserConfig]);

  useEffect(() => {
    if (!currentAccount) {
      toast.error('No account selected');
      navigate('/bill-jobs');
      return;
    }
    fetchDocument();
    fetchPdfAsBlob();
    fetchAccountUsers();
    fetchAuditLogs();
    fetchParserConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, currentAccount?.id, navigate]);

  const fetchParserConfig = async () => {
    if (!currentAccount) return;
    try {
      const { data } = await api.get(`/accounts/${currentAccount.id}/parsing-config`);
      setParserConfig(data);
    } catch (error) {
      console.error('Failed to load parser config');
    }
  };

  const fetchPdfAsBlob = async () => {
    if (!currentAccount) return;
    try {
      // First get file info (type and URL)
      const response = await api.get(
        `/accounts/${currentAccount.id}/documents/${documentId}/file`
      );
      
      let blob;
      if (response.data.type === 's3') {
        // Fetch from S3 presigned URL directly
        const s3Response = await fetch(response.data.url);
        blob = await s3Response.blob();
      } else {
        // Local file - use the download endpoint
        const blobResponse = await api.get(
          `/accounts/${currentAccount.id}/documents/${documentId}/file/download`,
          { responseType: 'blob' }
        );
        blob = new Blob([blobResponse.data], { type: 'application/pdf' });
      }
      
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
    } catch (error) {
      console.error('Failed to load PDF:', error);
      toast.error('Failed to load PDF file');
    }
  };

  const fetchDocument = async () => {
    if (!currentAccount) return;
    try {
      const { data } = await api.get(`/accounts/${currentAccount.id}/documents/${documentId}`);
      setDocument(data.document);
      
      // Set document workflow state
      setReviewStatus(data.document.review_status || 'review');
      setAssignedTo(data.document.assigned_to || null);
      
      // Check if BillPortal export exists
      setHasBillPortalExport(data.document.billportal_exported || false);
      
      // Set confidence scores
      if (data.confidence_scores) {
        setConfidenceScores(data.confidence_scores);
      }
      
      if (data.extracted_data) {
        const extracted = data.extracted_data;
        
        setStatement({
          property_name: extracted.property_name || '',
          vendor_name: extracted.vendor_name || '',
          commodity: extracted.commodity || 'Electricity',
          account_no: extracted.account_no || '',
          meter_number: extracted.meter_number || '',  // NEW: Primary meter number
          customer_name: extracted.customer_name || '',
          service_address: extracted.service_address || '',
          city: extracted.city || '',
          state: extracted.state || '',
          postcode: extracted.postcode || '',
          invoice_no: extracted.invoice_no || '',
          issue_date: extracted.issue_date || '',
          period_from: extracted.period_from || '',
          period_to: extracted.period_to || '',
          due_date: extracted.due_date || '',
          previous_balance: extracted.previous_balance || '',
          payments_received: extracted.payments_received || '',
          balance_forward: extracted.balance_forward || '',
          late_fee: extracted.late_fee || '',         // NEW: Late fee
          credits: extracted.credits || '',           // NEW: Credits
          adjustments: extracted.adjustments || '',   // NEW: Adjustments
          new_charges: extracted.new_charges || '',
          total_amount_due: extracted.total_amount_due || ''
        });
        
        // NEW: Set validation data if present
        if (extracted._validation) {
          setValidationData(extracted._validation);
        }
        
        // Load provider charges (for split-billing single bills)
        setProviderCharges(Array.isArray(extracted.provider_charges) ? extracted.provider_charges : []);
        
        // Normalize usage details to handle both single and consolidated bill formats
        const normalizedUsage = (Array.isArray(extracted.usage_details) ? extracted.usage_details : []).map(item => ({
          ...item,
          // Handle field name differences between single and consolidated bills
          usage: item.usage || item.usage_quantity || '',
          usage_rate: item.usage_rate || item.rate || '',
          charges: item.charges || item.line_item_charge || '',
          commodity: item.commodity || 'Electricity',
          usage_type: item.usage_type || 'Both',
          line_item_description: item.line_item_description || item.rate_period || '',
          is_total: item.is_total || false  // NEW: Flag for summed totals
        }));
        
        setMeterReads(Array.isArray(extracted.meter_reads) ? extracted.meter_reads : []);
        setUsageDetails(normalizedUsage);
        setOtherCharges(Array.isArray(extracted.other_charges) ? extracted.other_charges : []);
        setUsageBreakdown(Array.isArray(extracted._usage_breakdown) ? extracted._usage_breakdown : []);
        
        // Store validation data from AI
        if (extracted._validation) {
          setValidationData(extracted._validation);
        }
      }
    } catch (error) {
      toast.error('Failed to load document');
      navigate('/bill-jobs');
    } finally {
      setLoading(false);
    }
  };

  const fetchAccountUsers = async () => {
    if (!currentAccount) return;
    try {
      const { data } = await api.get(`/accounts/${currentAccount.id}/users-list`);
      setAccountUsers(data);
    } catch (error) {
      console.error('Failed to load users');
    }
  };

  const fetchAuditLogs = async () => {
    if (!currentAccount) return;
    try {
      const { data } = await api.get(`/accounts/${currentAccount.id}/documents/${documentId}/audit`);
      setAuditLogs(data);
    } catch (error) {
      console.error('Failed to load audit logs');
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await api.patch(`/accounts/${currentAccount.id}/documents/${documentId}/status`, {
        review_status: newStatus
      });
      setReviewStatus(newStatus);
      toast.success(`Status changed to ${newStatus}`);
      fetchAuditLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleAssignment = async (userId) => {
    try {
      await api.patch(`/accounts/${currentAccount.id}/documents/${documentId}/assign`, {
        user_id: userId || null
      });
      const assignedUser = accountUsers.find(u => u.id === userId);
      setAssignedTo(userId);
      toast.success(userId ? `Assigned to ${assignedUser?.name}` : 'Assignment removed');
      fetchDocument();
      fetchAuditLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign');
    }
  };

  const handleReparse = async (mode = 'all') => {
    setReparsing(true);
    try {
      await api.post(`/accounts/${currentAccount.id}/documents/${documentId}/reprocess`, {
        mode: mode  // 'all' or 'missing'
      });
      toast.success(mode === 'missing' ? 'Re-parsing missing fields. Please wait...' : 'Full re-parsing started. Please wait...');
      
      // Poll for completion
      const checkInterval = setInterval(async () => {
        const { data } = await api.get(`/accounts/${currentAccount.id}/documents/${documentId}`);
        if (data.document.status === 'completed') {
          clearInterval(checkInterval);
          setReparsing(false);
          toast.success('Re-parsing completed!');
          fetchDocument();
        } else if (data.document.status === 'failed') {
          clearInterval(checkInterval);
          setReparsing(false);
          toast.error('Re-parsing failed');
        }
      }, 3000);
      
      // Timeout after 2 minutes
      setTimeout(() => {
        clearInterval(checkInterval);
        setReparsing(false);
      }, 120000);
    } catch (error) {
      toast.error('Failed to start re-parsing');
      setReparsing(false);
    }
  };

  const handleDownloadJSON = async () => {
    if (!currentAccount || !documentId) return;
    try {
      // First check if export exists, if not create it
      if (!hasBillPortalExport) {
        toast.info('Converting to BillPortal format...');
        await api.post(`/accounts/${currentAccount.id}/parser-exports/${documentId}/convert`);
        setHasBillPortalExport(true);
      }
      
      // Fetch the parser export
      const { data } = await api.get(`/accounts/${currentAccount.id}/parser-exports`);
      const exportData = data.exports?.find(e => e.document_id === documentId);
      
      if (exportData && exportData.data) {
        // Create and download JSON file
        const jsonStr = JSON.stringify(exportData.data, null, 2);
        const blob = new Blob([jsonStr], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = window.document.createElement('a');
        link.href = url;
        link.download = `${document?.filename?.replace('.pdf', '') || 'bill'}_billportal.json`;
        link.click();
        URL.revokeObjectURL(url);
        toast.success('JSON downloaded');
      } else {
        toast.error('Export data not found');
      }
    } catch (error) {
      toast.error('Failed to download JSON');
      console.error(error);
    }
  };

  // Memoize PDF.js options to avoid unnecessary reloads
  const pdfOptions = useMemo(() => ({
    cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
    cMapPacked: true,
    standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts/`,
    wasmUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/wasm/`,
  }), []);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  // Navigate to specific page in PDF
  const navigateToPage = useCallback((pageNum) => {
    if (!pageNum || !numPages) return;
    const page = parseInt(pageNum, 10);
    if (page < 1 || page > numPages) return;
    
    setCurrentPage(page);
    
    // Scroll to the page element
    if (pdfContainerRef.current) {
      const pageElement = pdfContainerRef.current.querySelector(`[data-page-number="${page}"]`);
      if (pageElement) {
        pageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
    
    toast.info(`Navigating to page ${page}`, { duration: 1500 });
  }, [numPages]);

  // Handle row focus - highlight row and navigate to page (text highlighting disabled)
  const handleRowFocus = useCallback((rowId, pageNumber) => {
    setEditingRowId(rowId);
    if (pageNumber) {
      navigateToPage(pageNumber);
    }
  }, [navigateToPage]);

  // Mark row as reviewed/worked
  const toggleRowReviewed = useCallback((rowId) => {
    setReviewedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  }, []);

  // Count validation errors for badge
  const validationErrorCount = useMemo(() => {
    let count = 0;
    if (!balanceValidation.isValid) count++;
    if (meterValidation.hasErrors) count += meterValidation.errorMessages.length;
    if (usageValidation.hasErrors) count += usageValidation.errorMessages.length;
    if (uomValidation.hasErrors) count += uomValidation.errorMessages.length;
    return count;
  }, [balanceValidation, meterValidation, usageValidation, uomValidation]);

  const handleSave = async () => {
    if (!editMode) {
      // Entering edit mode - auto-assign to current user and set status to Review
      try {
        const { user } = getAuth();
        
        // Auto-assign to current user if not already assigned
        if (user && assignedTo !== user.id) {
          await api.patch(`/accounts/${currentAccount.id}/documents/${documentId}/assign`, {
            user_id: user.id
          });
          setAssignedTo(user.id);
        }
        
        // Set status to Review if it's Approved
        if (reviewStatus === 'approved') {
          await api.patch(`/accounts/${currentAccount.id}/documents/${documentId}/status`, {
            review_status: 'review'
          });
          setReviewStatus('review');
          toast.info('Document unlocked for editing');
        }
        
        setEditMode(true);
        fetchAuditLogs();
      } catch (error) {
        toast.error('Failed to start editing');
        return;
      }
      return;
    }
    
    setSaving(true);
    try {
      const updatedData = {
        ...statement,
        provider_charges: providerCharges,
        meter_reads: meterReads,
        usage_details: usageDetails,
        other_charges: otherCharges
      };
      
      const { data } = await api.put(
        `/accounts/${currentAccount.id}/documents/${documentId}/extracted-data`,
        updatedData
      );
      
      // Update confidence scores from response
      if (data.confidence_scores) {
        setConfidenceScores(data.confidence_scores);
      }
      
      toast.success('Changes saved and validated');
      setEditMode(false);
      
      // Refresh document to get updated data
      fetchDocument();
      fetchAuditLogs();
    } catch (error) {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  // Meter Reads functions
  const addMeterRead = () => {
    setMeterReads([...meterReads, {
      meter_number: '',
      commodity: 'Electricity',
      multiplier: 'N/A',
      previous_read_date: '',
      current_read_date: '',
      previous_read: '',
      current_read: '',
      read_type: 'Actual'
    }]);
  };

  const updateMeterRead = (index, field, value) => {
    const updated = [...meterReads];
    updated[index] = { ...updated[index], [field]: value };
    setMeterReads(updated);
  };

  const removeMeterRead = (index) => {
    setMeterReads(meterReads.filter((_, i) => i !== index));
  };

  // Usage Details functions
  const addUsageDetail = () => {
    setUsageDetails([...usageDetails, {
      meter_number: '',
      line_item_description: '',
      usage: '',
      uom: 'kWh',
      usage_rate: '',
      charges: ''
    }]);
  };

  const updateUsageDetail = (index, field, value) => {
    const updated = [...usageDetails];
    updated[index] = { ...updated[index], [field]: value };
    setUsageDetails(updated);
  };

  const removeUsageDetail = (index) => {
    setUsageDetails(usageDetails.filter((_, i) => i !== index));
  };

  // Other Charges functions
  const addOtherCharge = () => {
    setOtherCharges([...otherCharges, {
      line_item_description: '',
      usage: '',
      rate: '',
      charge: ''
    }]);
  };

  const updateOtherCharge = (index, field, value) => {
    const updated = [...otherCharges];
    updated[index] = { ...updated[index], [field]: value };
    setOtherCharges(updated);
  };

  const removeOtherCharge = (index) => {
    setOtherCharges(otherCharges.filter((_, i) => i !== index));
  };

  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Failed Document Banner */}
      {document?.status === 'failed' && (
        <div className="bg-destructive/10 border-b border-destructive/20 px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-destructive" />
            <div>
              <p className="text-sm font-medium text-destructive">Document Extraction Failed</p>
              <p className="text-xs text-muted-foreground">The PDF could not be automatically parsed. You can re-parse or manually enter the data.</p>
            </div>
          </div>
          <Button
            size="sm"
            className="rounded-none"
            onClick={handleReparse}
            disabled={reparsing}
          >
            {reparsing ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            {reparsing ? 'Re-parsing...' : 'Try Again'}
          </Button>
        </div>
      )}
      
      {/* Header */}
      <header className="border-b border-border bg-card px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            className="rounded-none"
            onClick={() => navigate('/bill-jobs')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5" strokeWidth={1.5} />
            <h1 className="font-mono text-sm font-medium">{document?.filename}</h1>
            {hasBillPortalExport && (
              <Badge variant="outline" className="rounded-none text-[10px] px-1 py-0 bg-emerald-50 text-emerald-700 border-emerald-200">
                BP
              </Badge>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Download PDF Button */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (pdfUrl) {
                      const link = window.document.createElement('a');
                      link.href = pdfUrl;
                      link.download = document?.filename || 'bill.pdf';
                      link.click();
                    }
                  }}
                  disabled={!pdfUrl}
                  className="rounded-none"
                  data-testid="download-bill"
                >
                  <Download className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs">Download PDF</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          {/* Download JSON Button */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadJSON}
                  disabled={document?.status !== 'completed'}
                  className="rounded-none"
                  data-testid="download-json"
                >
                  <FileText className="h-4 w-4" />
                  <span className="ml-1 text-xs">JSON</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs">{hasBillPortalExport ? 'Download BillPortal JSON' : 'Convert & Download JSON'}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="rounded-none"
                disabled={reparsing || document?.status === 'processing'}
              >
                {reparsing || document?.status === 'processing' ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                {reparsing ? 'Re-parsing...' : document?.status === 'processing' ? 'Processing...' : 'Re-parse'}
                <ChevronDown className="h-4 w-4 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="rounded-none">
              <DropdownMenuItem onClick={() => handleReparse('all')} className="cursor-pointer">
                <RefreshCw className="h-4 w-4 mr-2" />
                Re-parse All Fields
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleReparse('missing')} className="cursor-pointer">
                <AlertCircle className="h-4 w-4 mr-2" />
                Fill Missing Fields Only
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          {/* Audit Trail Button */}
          <Button
            variant="outline"
            size="sm"
            className="rounded-none"
            onClick={() => { fetchAuditLogs(); setShowAuditModal(true); }}
          >
            <History className="h-4 w-4 mr-2" />
            History
          </Button>
          
          {editMode && (
            <Button
              variant="outline"
              size="sm"
              className="rounded-none"
              onClick={() => {
                setEditMode(false);
                fetchDocument();
              }}
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
          )}
          <Button
            variant={editMode ? 'default' : 'outline'}
            size="sm"
            className="rounded-none font-bold uppercase"
            onClick={handleSave}
            disabled={saving || reviewStatus === 'approved'}
            title={reviewStatus === 'approved' ? 'Document is approved and locked for editing' : ''}
          >
            {reviewStatus === 'approved' ? (
              <Lock className="h-4 w-4 mr-2" />
            ) : saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : editMode ? (
              <Save className="h-4 w-4 mr-2" />
            ) : (
              <Edit className="h-4 w-4 mr-2" />
            )}
            {reviewStatus === 'approved' ? 'Locked' : saving ? 'Saving...' : editMode ? 'Save' : 'Edit'}
          </Button>
        </div>
      </header>
      
      {/* Workflow Bar */}
      <div className="border-b border-border bg-card px-6 py-2 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Label className="text-xs uppercase font-medium">Status:</Label>
            <Select value={reviewStatus} onValueChange={handleStatusChange}>
              <SelectTrigger className="w-32 h-8 rounded-none text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="review">Review</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs uppercase font-medium">Assigned:</Label>
            <Select value={assignedTo || '__unassigned__'} onValueChange={(value) => handleAssignment(value === '__unassigned__' ? null : value)}>
              <SelectTrigger className="w-40 h-8 rounded-none text-xs">
                <SelectValue placeholder="Unassigned" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__unassigned__">Unassigned</SelectItem>
                {accountUsers.map((user) => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {reviewStatus === 'approved' && (
          <Badge className="rounded-none bg-blue-600">
            <Lock className="h-3 w-3 mr-1" />
            Approved & Locked
          </Badge>
        )}
      </div>

      {/* Split View */}
      <ResizablePanelGroup direction="horizontal" className="flex-1">
        {/* PDF Panel */}
        <ResizablePanel defaultSize={45} minSize={30}>
          <div className="h-full flex flex-col bg-muted/30">
            {/* PDF Controls */}
            <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-card">
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setScale(s => Math.max(0.5, s - 0.1))}
                  className="rounded-none h-8 w-8 p-0"
                  data-testid="zoom-out"
                >
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <span className="text-sm font-mono w-14 text-center">{Math.round(scale * 100)}%</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setScale(s => Math.min(2.0, s + 0.1))}
                  className="rounded-none h-8 w-8 p-0"
                  data-testid="zoom-in"
                >
                  <ZoomIn className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setScale(1.0)}
                  className="rounded-none text-xs"
                >
                  Reset
                </Button>
              </div>
              
              {/* Rotation Controls */}
              <div className="flex items-center gap-1 border-l border-border pl-2 ml-2">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setRotation(r => (r - 90 + 360) % 360)}
                        className="rounded-none h-8 w-8 p-0"
                        data-testid="rotate-ccw"
                      >
                        <RotateCcw className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Rotate Left</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <span className="text-xs font-mono w-10 text-center">{rotation}°</span>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setRotation(r => (r + 90) % 360)}
                        className="rounded-none h-8 w-8 p-0"
                        data-testid="rotate-cw"
                      >
                        <RotateCw className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Rotate Right</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              
              <div className="flex items-center gap-2">
                {numPages && (
                  <span className="text-xs text-muted-foreground">
                    {numPages} page{numPages > 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </div>
            
            {/* JPEG2000 Warning Banner */}
            {hasJpeg2000Warning && (
              <div className="mx-4 mb-2 p-3 bg-yellow-50 border border-yellow-200 rounded-none">
                <div className="flex items-center gap-2 text-yellow-800">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                  <div className="text-xs">
                    <p className="font-medium">Some images in this PDF may not display</p>
                    <p className="text-yellow-700">This PDF uses JPEG2000 encoding which has limited browser support. The data extraction is complete.</p>
                    <Button 
                      variant="link" 
                      size="sm" 
                      className="h-auto p-0 text-yellow-800 underline"
                      onClick={() => pdfUrl && window.open(pdfUrl, '_blank')}
                    >
                      Download PDF to view in desktop viewer
                    </Button>
                  </div>
                </div>
              </div>
            )}
            
            {/* PDF Content */}
            <div className="flex-1 overflow-auto p-4" ref={pdfContainerRef}>
              <div className="mx-auto" style={{ maxWidth: `${Math.round(650 * scale)}px` }}>
                {pdfUrl ? (
                  <Document
                    file={pdfUrl}
                    onLoadSuccess={onDocumentLoadSuccess}
                    options={pdfOptions}
                    loading={
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                      </div>
                    }
                    error={
                      <div className="flex flex-col items-center justify-center py-12 text-center px-4">
                        <FileText className="h-12 w-12 mb-4 text-muted-foreground" />
                        <p className="text-muted-foreground mb-2">Failed to load PDF</p>
                        {pdfLoadError && (
                          <p className="text-xs text-muted-foreground max-w-xs">{pdfLoadError}</p>
                        )}
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="mt-4"
                          onClick={() => pdfUrl && window.open(pdfUrl, '_blank')}
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Download PDF
                        </Button>
                      </div>
                    }
                    onLoadError={(error) => {
                      console.error('PDF Load Error:', error);
                      setPdfLoadError(error?.message || 'Unknown error');
                    }}
                  >
                    {Array.from(new Array(numPages), (el, index) => (
                      <Page
                        key={`page_${index + 1}`}
                        pageNumber={index + 1}
                        className={`mb-4 border border-border transition-all ${currentPage === index + 1 ? 'ring-4 ring-blue-500 ring-offset-2' : ''}`}
                        width={Math.round(650 * scale)}
                        scale={scale}
                        rotate={rotation}
                        renderTextLayer={true}
                        renderAnnotationLayer={false}
                        data-page-number={index + 1}
                        onRenderError={(error) => {
                          console.warn('Page render error:', error);
                          // Check if it's a JPEG2000/OpenJPEG error
                          if (error?.message?.includes('JpxError') || error?.message?.includes('OpenJPEG')) {
                            setHasJpeg2000Warning(true);
                          }
                        }}
                      />
                    ))}
                  </Document>
                ) : (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </ResizablePanel>

        <ResizableHandle className="w-1 bg-border hover:bg-primary/50 transition-colors" />

        {/* Data Panel with Tabs */}
        <ResizablePanel defaultSize={55} minSize={40}>
          <div className="h-full overflow-auto bg-background">
            <Tabs defaultValue="statement" className="w-full h-full flex flex-col">
              <TabsList className={`w-full grid ${intelligentValidation.enabled ? 'grid-cols-5' : 'grid-cols-4'} rounded-none border-b`}>
                <TabsTrigger value="statement" className="rounded-none">Statement</TabsTrigger>
                <TabsTrigger value="meter-reads" className="rounded-none">Meter Reads</TabsTrigger>
                <TabsTrigger value="usage" className="rounded-none">Usage Details</TabsTrigger>
                <TabsTrigger value="other" className="rounded-none">Other Charges</TabsTrigger>
                {intelligentValidation.enabled && (
                  <TabsTrigger value="validation" className="rounded-none" data-testid="validation-tab-trigger">
                    <span className="flex items-center gap-1">
                      Validation
                      {intelligentValidation.validationMatchesNewCharges ? (
                        <Check className="h-3 w-3 text-green-600" />
                      ) : (
                        <AlertTriangle className="h-3 w-3 text-yellow-600" />
                      )}
                    </span>
                  </TabsTrigger>
                )}
              </TabsList>

              {/* Statement Tab */}
              <TabsContent value="statement" className="flex-1 overflow-auto p-6">
                <h3 className="text-lg font-bold mb-4">Bill Statement</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs uppercase tracking-wide font-medium">Property Name</Label>
                      <ConfidenceIndicator confidence={confidenceScores.property_name} field="property_name" />
                    </div>
                    <Input
                      className="rounded-none mt-1 font-mono text-sm"
                      value={statement.property_name}
                      onChange={(e) => setStatement({ ...statement, property_name: e.target.value })}
                      disabled={!editMode}
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs uppercase tracking-wide font-medium">Vendor Name</Label>
                      <ConfidenceIndicator confidence={confidenceScores.vendor_name} field="vendor_name" />
                    </div>
                    <Input
                      className="rounded-none mt-1 font-mono text-sm"
                      value={statement.vendor_name}
                      onChange={(e) => setStatement({ ...statement, vendor_name: e.target.value })}
                      disabled={!editMode}
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs uppercase tracking-wide font-medium">Commodity</Label>
                      <ConfidenceIndicator confidence={confidenceScores.commodity} field="commodity" />
                    </div>
                    <Select
                      value={statement.commodity}
                      onValueChange={(value) => setStatement({ ...statement, commodity: value })}
                      disabled={!editMode}
                    >
                      <SelectTrigger className="rounded-none mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Electricity">Electricity</SelectItem>
                        <SelectItem value="Gas">Gas</SelectItem>
                        <SelectItem value="Water">Water</SelectItem>
                        <SelectItem value="Sewer">Sewer</SelectItem>
                        <SelectItem value="Waste">Waste</SelectItem>
                        <SelectItem value="Internet">Internet</SelectItem>
                        <SelectItem value="Heating Oil">Heating Oil</SelectItem>
                        <SelectItem value="Phone">Phone</SelectItem>
                        <SelectItem value="Cable/TV">Cable/TV</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs uppercase tracking-wide font-medium">Account No</Label>
                      <ConfidenceIndicator confidence={confidenceScores.account_no} field="account_no" />
                    </div>
                    <Input
                      className="rounded-none mt-1 font-mono text-sm"
                      value={statement.account_no}
                      onChange={(e) => setStatement({ ...statement, account_no: e.target.value })}
                      disabled={!editMode}
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs uppercase tracking-wide font-medium">Meter Number</Label>
                      <ConfidenceIndicator confidence={confidenceScores.meter_number} field="meter_number" />
                    </div>
                    <Input
                      className="rounded-none mt-1 font-mono text-sm"
                      value={statement.meter_number}
                      onChange={(e) => setStatement({ ...statement, meter_number: e.target.value })}
                      disabled={!editMode}
                      placeholder="Primary meter ID"
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs uppercase tracking-wide font-medium">Customer Name</Label>
                      <ConfidenceIndicator confidence={confidenceScores.customer_name} field="customer_name" />
                    </div>
                    <Input
                      className="rounded-none mt-1 font-mono text-sm"
                      value={statement.customer_name}
                      onChange={(e) => setStatement({ ...statement, customer_name: e.target.value })}
                      disabled={!editMode}
                    />
                  </div>
                  <div className="col-span-2">
                    <div className="flex items-center gap-2">
                      <Label className="text-xs uppercase tracking-wide font-medium">Service Address</Label>
                      <ConfidenceIndicator confidence={confidenceScores.service_address} field="service_address" />
                    </div>
                    <Input
                      className="rounded-none mt-1 font-mono text-sm"
                      value={statement.service_address}
                      onChange={(e) => setStatement({ ...statement, service_address: e.target.value })}
                      disabled={!editMode}
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs uppercase tracking-wide font-medium">City</Label>
                      <ConfidenceIndicator confidence={confidenceScores.city} field="city" />
                    </div>
                    <Input
                      className="rounded-none mt-1 font-mono text-sm"
                      value={statement.city}
                      onChange={(e) => setStatement({ ...statement, city: e.target.value })}
                      disabled={!editMode}
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs uppercase tracking-wide font-medium">State</Label>
                      <ConfidenceIndicator confidence={confidenceScores.state} field="state" />
                    </div>
                    <Input
                      className="rounded-none mt-1 font-mono text-sm"
                      value={statement.state}
                      onChange={(e) => setStatement({ ...statement, state: e.target.value })}
                      disabled={!editMode}
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs uppercase tracking-wide font-medium">Postcode</Label>
                      <ConfidenceIndicator confidence={confidenceScores.postcode} field="postcode" />
                    </div>
                    <Input
                      className="rounded-none mt-1 font-mono text-sm"
                      value={statement.postcode}
                      onChange={(e) => setStatement({ ...statement, postcode: e.target.value })}
                      disabled={!editMode}
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs uppercase tracking-wide font-medium">Invoice No</Label>
                      <ConfidenceIndicator confidence={confidenceScores.invoice_no} field="invoice_no" />
                    </div>
                    <Input
                      className="rounded-none mt-1 font-mono text-sm"
                      value={statement.invoice_no}
                      onChange={(e) => setStatement({ ...statement, invoice_no: e.target.value })}
                      disabled={!editMode}
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs uppercase tracking-wide font-medium">Issue Date</Label>
                      <ConfidenceIndicator confidence={confidenceScores.issue_date} field="issue_date" />
                    </div>
                    <Input
                      type="date"
                      className="rounded-none mt-1 font-mono text-sm"
                      value={statement.issue_date}
                      onChange={(e) => setStatement({ ...statement, issue_date: e.target.value })}
                      disabled={!editMode}
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs uppercase tracking-wide font-medium">Period From</Label>
                      <ConfidenceIndicator confidence={confidenceScores.period_from} field="period_from" />
                    </div>
                    <Input
                      type="date"
                      className="rounded-none mt-1 font-mono text-sm"
                      value={statement.period_from}
                      onChange={(e) => setStatement({ ...statement, period_from: e.target.value })}
                      disabled={!editMode}
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs uppercase tracking-wide font-medium">Period To</Label>
                      <ConfidenceIndicator confidence={confidenceScores.period_to} field="period_to" />
                    </div>
                    <Input
                      type="date"
                      className="rounded-none mt-1 font-mono text-sm"
                      value={statement.period_to}
                      onChange={(e) => setStatement({ ...statement, period_to: e.target.value })}
                      disabled={!editMode}
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs uppercase tracking-wide font-medium">Due Date</Label>
                      <ConfidenceIndicator confidence={confidenceScores.due_date} field="due_date" />
                    </div>
                    <Input
                      type="date"
                      className="rounded-none mt-1 font-mono text-sm"
                      value={statement.due_date}
                      onChange={(e) => setStatement({ ...statement, due_date: e.target.value })}
                      disabled={!editMode}
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs uppercase tracking-wide font-medium">Previous Balance</Label>
                      <ConfidenceIndicator confidence={confidenceScores.previous_balance} field="previous_balance" />
                    </div>
                    <Input
                      className="rounded-none mt-1 font-mono text-sm"
                      value={statement.previous_balance}
                      onChange={(e) => setStatement({ ...statement, previous_balance: e.target.value })}
                      disabled={!editMode}
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs uppercase tracking-wide font-medium">Payments Received</Label>
                      <ConfidenceIndicator confidence={confidenceScores.payments_received} field="payments_received" />
                    </div>
                    <Input
                      className="rounded-none mt-1 font-mono text-sm"
                      value={statement.payments_received}
                      onChange={(e) => setStatement({ ...statement, payments_received: e.target.value })}
                      disabled={!editMode}
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs uppercase tracking-wide font-medium">Balance Forward</Label>
                      <ConfidenceIndicator confidence={confidenceScores.balance_forward} field="balance_forward" />
                    </div>
                    <Input
                      className="rounded-none mt-1 font-mono text-sm"
                      value={statement.balance_forward}
                      onChange={(e) => setStatement({ ...statement, balance_forward: e.target.value })}
                      disabled={!editMode}
                    />
                  </div>
                  
                  {/* Late Fee, Credits, Adjustments Row */}
                  <div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs uppercase tracking-wide font-medium">Late Fee</Label>
                      <ConfidenceIndicator confidence={confidenceScores.late_fee} field="late_fee" />
                    </div>
                    <Input
                      className="rounded-none mt-1 font-mono text-sm"
                      value={statement.late_fee}
                      onChange={(e) => setStatement({ ...statement, late_fee: e.target.value })}
                      disabled={!editMode}
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs uppercase tracking-wide font-medium">Credits</Label>
                      <ConfidenceIndicator confidence={confidenceScores.credits} field="credits" />
                    </div>
                    <Input
                      className="rounded-none mt-1 font-mono text-sm"
                      value={statement.credits}
                      onChange={(e) => setStatement({ ...statement, credits: e.target.value })}
                      disabled={!editMode}
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs uppercase tracking-wide font-medium">Adjustments</Label>
                      <ConfidenceIndicator confidence={confidenceScores.adjustments} field="adjustments" />
                    </div>
                    <Input
                      className="rounded-none mt-1 font-mono text-sm"
                      value={statement.adjustments}
                      onChange={(e) => setStatement({ ...statement, adjustments: e.target.value })}
                      disabled={!editMode}
                      placeholder="0.00"
                    />
                  </div>
                  
                  <div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs uppercase tracking-wide font-medium">New Charges</Label>
                      <ConfidenceIndicator confidence={confidenceScores.new_charges} field="new_charges" />
                    </div>
                    <Input
                      className="rounded-none mt-1 font-mono text-sm"
                      value={statement.new_charges}
                      onChange={(e) => setStatement({ ...statement, new_charges: e.target.value })}
                      disabled={!editMode}
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs uppercase tracking-wide font-medium">Total Amount Due</Label>
                      <ConfidenceIndicator confidence={confidenceScores.total_amount_due} field="total_amount_due" />
                    </div>
                    <Input
                      className="rounded-none mt-1 font-mono text-sm font-bold"
                      value={statement.total_amount_due}
                      onChange={(e) => setStatement({ ...statement, total_amount_due: e.target.value })}
                      disabled={!editMode}
                    />
                  </div>
                </div>
                
                {/* Provider Charges Section (for split-billing bills) */}
                {providerCharges.length > 0 && (
                  <div className="mt-8">
                    <h4 className="text-md font-bold mb-3 flex items-center gap-2">
                      Provider Charges
                      <Badge variant="secondary" className="rounded-none text-xs">
                        {providerCharges.length} Provider{providerCharges.length > 1 ? 's' : ''}
                      </Badge>
                    </h4>
                    <div className="overflow-x-auto border border-border">
                      <table className="w-full border-collapse" style={{ minWidth: '600px' }}>
                        <thead className="bg-muted">
                          <tr>
                            <th className="border border-border p-2 text-left text-xs font-medium">Provider</th>
                            <th className="border border-border p-2 text-left text-xs font-medium">Type</th>
                            <th className="border border-border p-2 text-left text-xs font-medium">Invoice No</th>
                            <th className="border border-border p-2 text-right text-xs font-medium">New Charges</th>
                            <th className="border border-border p-2 text-right text-xs font-medium">Tax</th>
                            <th className="border border-border p-2 text-right text-xs font-medium">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {providerCharges.map((provider, index) => (
                            <tr key={index}>
                              <td className="border border-border p-2 text-sm font-medium">{provider.provider_name || '-'}</td>
                              <td className="border border-border p-2 text-sm">
                                <Badge variant="outline" className="rounded-none text-xs">
                                  {provider.service_type || 'Both'}
                                </Badge>
                              </td>
                              <td className="border border-border p-2 text-sm font-mono">{provider.invoice_no || '-'}</td>
                              <td className="border border-border p-2 text-sm text-right font-mono">
                                {provider.new_charges ? `$${parseFloat(provider.new_charges).toFixed(2)}` : '-'}
                              </td>
                              <td className="border border-border p-2 text-sm text-right font-mono">
                                {provider.sales_tax ? `$${parseFloat(provider.sales_tax).toFixed(2)}` : '-'}
                              </td>
                              <td className="border border-border p-2 text-sm text-right font-mono font-medium">
                                {provider.total_charges ? `$${parseFloat(provider.total_charges).toFixed(2)}` : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </TabsContent>

              {/* Meter Reads Tab */}
              <TabsContent value="meter-reads" className="flex-1 overflow-auto p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-bold">Meter Reads</h3>
                  </div>
                  {editMode && (
                    <Button size="sm" variant="outline" className="rounded-none" onClick={addMeterRead}>
                      <Plus className="h-4 w-4 mr-1" /> Add Row
                    </Button>
                  )}
                </div>
                
                {meterReads.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-12">No meter reads data</p>
                ) : (
                  <>
                    <div className="overflow-x-auto border border-border">
                      <table className="border-collapse" style={{ minWidth: '1100px' }}>
                        <thead className="bg-muted">
                          <tr>
                            <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '50px' }}>Page</th>
                            <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '110px' }}>Meter No</th>
                            <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '90px' }}>Commodity</th>
                            <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '80px' }}>Multiplier</th>
                            <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '100px' }}>Prev Date</th>
                            <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '100px' }}>Curr Date</th>
                            <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '90px' }}>Prev Read</th>
                            <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '90px' }}>Curr Read</th>
                            <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '90px' }}>Type</th>
                            {editMode && <th className="border border-border p-2 w-20 text-center text-xs font-medium">Actions</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {meterReads.map((read, index) => {
                            const rowId = `meter-${index}`;
                            const isRowActive = editingRowId === rowId;
                            const isRowReviewed = reviewedRows.has(rowId);
                            return (
                              <tr 
                                key={index}
                                className={`transition-colors ${isRowActive ? 'bg-blue-50 ring-2 ring-blue-400 ring-inset' : ''} ${isRowReviewed ? 'bg-green-50' : ''} ${!editMode && read.page_number ? 'cursor-pointer hover:bg-muted/50' : ''}`}
                                onClick={() => !editMode && read.page_number && navigateToPage(read.page_number, read.meter_number)}
                                title={read.page_number ? `Click to go to page ${read.page_number}` : ''}
                              >
                                <td className="border border-border p-1 text-center">
                                  {read.page_number && (
                                    <Badge 
                                      variant="outline" 
                                      className={`rounded-none text-xs font-mono cursor-pointer hover:bg-muted ${isRowActive ? 'bg-blue-100' : ''}`}
                                      onClick={(e) => { e.stopPropagation(); navigateToPage(read.page_number, read.meter_number); }}
                                    >
                                      p.{read.page_number}
                                    </Badge>
                                  )}
                                </td>
                                <td className="border border-border p-1">
                                  <Input
                                    className="rounded-none border-0 font-mono text-xs"
                                    value={read.meter_number || ''}
                                    onChange={(e) => updateMeterRead(index, 'meter_number', e.target.value)}
                                    onFocus={() => editMode && handleRowFocus(rowId, read.page_number)}
                                    disabled={!editMode}
                                  />
                                </td>
                                <td className="border border-border p-1">
                                  <Select
                                    value={read.commodity || 'Electricity'}
                                    onValueChange={(value) => updateMeterRead(index, 'commodity', value)}
                                    onOpenChange={(open) => open && editMode && handleRowFocus(rowId, read.page_number)}
                                    disabled={!editMode}
                                  >
                                    <SelectTrigger className="rounded-none border-0 h-8 text-xs">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="Electricity">Electricity</SelectItem>
                                      <SelectItem value="Gas">Gas</SelectItem>
                                      <SelectItem value="Water">Water</SelectItem>
                                      <SelectItem value="Sewer">Sewer</SelectItem>
                                      <SelectItem value="Waste">Waste</SelectItem>
                                      <SelectItem value="Internet">Internet</SelectItem>
                                      <SelectItem value="Heating Oil">Heating Oil</SelectItem>
                                      <SelectItem value="Other">Other</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </td>
                                <td className="border border-border p-1">
                                  <Input
                                    className="rounded-none border-0 font-mono text-xs"
                                    value={read.multiplier || 'N/A'}
                                    onChange={(e) => updateMeterRead(index, 'multiplier', e.target.value)}
                                    onFocus={() => editMode && handleRowFocus(rowId, read.page_number)}
                                    disabled={!editMode}
                                    placeholder="N/A"
                                  />
                                </td>
                                <td className="border border-border p-1">
                                  <Input
                                    type="date"
                                    className="rounded-none border-0 font-mono text-xs"
                                    value={read.previous_read_date || ''}
                                    onChange={(e) => updateMeterRead(index, 'previous_read_date', e.target.value)}
                                    onFocus={() => editMode && handleRowFocus(rowId, read.page_number)}
                                    disabled={!editMode}
                                  />
                                </td>
                                <td className="border border-border p-1">
                                  <Input
                                    type="date"
                                    className="rounded-none border-0 font-mono text-xs"
                                    value={read.current_read_date || ''}
                                    onChange={(e) => updateMeterRead(index, 'current_read_date', e.target.value)}
                                    disabled={!editMode}
                                  />
                                </td>
                                <td className="border border-border p-1">
                                  <Input
                                    className="rounded-none border-0 font-mono text-xs"
                                    value={read.previous_read || ''}
                                    onChange={(e) => updateMeterRead(index, 'previous_read', e.target.value)}
                                    onFocus={() => editMode && handleRowFocus(rowId, read.page_number)}
                                    disabled={!editMode}
                                  />
                                </td>
                                <td className="border border-border p-1">
                                  <Input
                                    className="rounded-none border-0 font-mono text-xs"
                                    value={read.current_read || ''}
                                    onChange={(e) => updateMeterRead(index, 'current_read', e.target.value)}
                                    onFocus={() => editMode && handleRowFocus(rowId, read.page_number)}
                                    disabled={!editMode}
                                  />
                                </td>
                                <td className="border border-border p-1">
                                  <Select
                                    value={read.read_type || 'Actual'}
                                    onValueChange={(value) => updateMeterRead(index, 'read_type', value)}
                                    onOpenChange={(open) => open && editMode && handleRowFocus(rowId, read.page_number)}
                                    disabled={!editMode}
                                  >
                                    <SelectTrigger className="rounded-none border-0 h-8 text-xs">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="Actual">Actual</SelectItem>
                                      <SelectItem value="Estimated">Estimated</SelectItem>
                                      <SelectItem value="Customer">Customer</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </td>
                                {editMode && (
                                  <td className="border border-border p-1 text-center">
                                    <div className="flex items-center justify-center gap-1">
                                      <Button
                                        size="sm"
                                        variant={isRowReviewed ? "default" : "outline"}
                                        className={`h-6 w-6 p-0 rounded-none ${isRowReviewed ? 'bg-green-600 hover:bg-green-700' : ''}`}
                                        onClick={() => toggleRowReviewed(rowId)}
                                        title={isRowReviewed ? 'Marked as reviewed' : 'Mark as reviewed'}
                                      >
                                        <Check className="h-3 w-3" />
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-6 w-6 p-0"
                                        onClick={() => removeMeterRead(index)}
                                        title="Delete row"
                                      >
                                        <X className="h-4 w-4 text-red-500" />
                                      </Button>
                                    </div>
                                  </td>
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </TabsContent>

              {/* Usage Details Tab */}
              <TabsContent value="usage" className="flex-1 overflow-auto p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-bold">Usage Details</h3>
                  </div>
                  {editMode && (
                    <Button size="sm" variant="outline" className="rounded-none" onClick={addUsageDetail}>
                      <Plus className="h-4 w-4 mr-1" /> Add Row
                    </Button>
                  )}
                </div>
                
                {usageDetails.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-12">No usage details</p>
                ) : (
                  <>
                    <div className="overflow-x-auto border border-border">
                      <table className="border-collapse" style={{ minWidth: '1200px' }}>
                        <thead className="bg-muted">
                          <tr>
                            <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '50px' }}>Page</th>
                            <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '100px' }}>Meter No</th>
                            <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '90px' }}>Commodity</th>
                            <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '80px' }}>Type</th>
                            <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '180px' }}>Description</th>
                            <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '80px' }}>Usage</th>
                            <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '70px' }}>UOM</th>
                            <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '80px' }}>Rate</th>
                            <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '90px' }}>Charge</th>
                            {editMode && <th className="border border-border p-2 w-20 text-center text-xs font-medium">Actions</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {usageDetails.map((item, index) => {
                            const rowId = `usage-${index}`;
                            const usageRowValidation = usageValidation.validations.find(v => v.index === index);
                            const uomRowValidation = uomValidation.validations.find(v => v.index === index);
                            const hasCalcError = usageRowValidation && !usageRowValidation.isValid;
                            const hasUomError = uomRowValidation && !uomRowValidation.isValid;
                            const isRowActive = editingRowId === rowId;
                            const isRowReviewed = reviewedRows.has(rowId);
                            return (
                              <tr 
                                key={index}
                                className={`transition-colors ${isRowActive ? 'bg-blue-50 ring-2 ring-blue-400 ring-inset' : ''} ${isRowReviewed ? 'bg-green-50' : ''} ${(hasCalcError || hasUomError) && showValidationWarnings && !isRowActive && !isRowReviewed ? (hasCalcError ? 'bg-yellow-50' : 'bg-orange-50') : ''} ${!editMode && item.page_number ? 'cursor-pointer hover:bg-muted/50' : ''}`}
                                onClick={() => !editMode && item.page_number && navigateToPage(item.page_number, item.line_item_description || item.meter_number)}
                                title={item.page_number ? `Click to go to page ${item.page_number}` : ''}
                              >
                                <td className="border border-border p-1 text-center">
                                  {item.page_number && (
                                    <Badge 
                                      variant="outline" 
                                      className={`rounded-none text-xs font-mono cursor-pointer hover:bg-muted ${isRowActive ? 'bg-blue-100' : ''}`}
                                      onClick={(e) => { e.stopPropagation(); navigateToPage(item.page_number, item.line_item_description || item.meter_number); }}
                                    >
                                      p.{item.page_number}
                                    </Badge>
                                  )}
                            </td>
                            <td className="border border-border p-1">
                              <Input
                                className="rounded-none border-0 font-mono text-xs"
                                value={item.meter_number || ''}
                                onChange={(e) => updateUsageDetail(index, 'meter_number', e.target.value)}
                                onFocus={() => editMode && handleRowFocus(rowId, item.page_number)}
                                disabled={!editMode}
                              />
                            </td>
                            <td className="border border-border p-1">
                              <Select
                                value={item.commodity || 'Electricity'}
                                onValueChange={(value) => updateUsageDetail(index, 'commodity', value)}
                                disabled={!editMode}
                              >
                                <SelectTrigger className="rounded-none border-0 h-8 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="Electricity">Electricity</SelectItem>
                                  <SelectItem value="Gas">Gas</SelectItem>
                                  <SelectItem value="Water">Water</SelectItem>
                                  <SelectItem value="Sewer">Sewer</SelectItem>
                                  <SelectItem value="Waste">Waste</SelectItem>
                                  <SelectItem value="Internet">Internet</SelectItem>
                                  <SelectItem value="Heating Oil">Heating Oil</SelectItem>
                                  <SelectItem value="Phone">Phone</SelectItem>
                                  <SelectItem value="Cable/TV">Cable/TV</SelectItem>
                                  <SelectItem value="Other">Other</SelectItem>
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="border border-border p-1">
                              <Select
                                value={item.usage_type || 'Both'}
                                onValueChange={(value) => updateUsageDetail(index, 'usage_type', value)}
                                disabled={!editMode}
                              >
                                <SelectTrigger className="rounded-none border-0 h-8 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="Supply">Supply</SelectItem>
                                  <SelectItem value="Delivery">Delivery</SelectItem>
                                  <SelectItem value="Both">Both</SelectItem>
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="border border-border p-1">
                              <Input
                                className="rounded-none border-0 text-xs"
                                value={item.line_item_description || ''}
                                onChange={(e) => updateUsageDetail(index, 'line_item_description', e.target.value)}
                                disabled={!editMode}
                              />
                            </td>
                            <td className="border border-border p-1">
                              <Input
                                className="rounded-none border-0 font-mono text-xs"
                                value={item.usage || ''}
                                onChange={(e) => updateUsageDetail(index, 'usage', e.target.value)}
                                disabled={!editMode}
                              />
                            </td>
                            <td className="border border-border p-1">
                              {editMode ? (
                                <Select
                                  value={item.uom || 'kWh'}
                                  onValueChange={(value) => updateUsageDetail(index, 'uom', value)}
                                >
                                  <SelectTrigger className="rounded-none border-0 h-8 text-xs">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="kWh">kWh</SelectItem>
                                    <SelectItem value="Therms">Therms</SelectItem>
                                    <SelectItem value="CCF">CCF</SelectItem>
                                    <SelectItem value="Gallons">Gallons</SelectItem>
                                    <SelectItem value="MJ">MJ</SelectItem>
                                    <SelectItem value="Kg">Kg</SelectItem>
                                    <SelectItem value="tonnes">tonnes</SelectItem>
                                  </SelectContent>
                                </Select>
                              ) : (
                                <Input
                                  className="rounded-none border-0 font-mono text-xs"
                                  value={item.uom || ''}
                                  disabled
                                />
                              )}
                            </td>
                            <td className="border border-border p-1">
                              <Input
                                className="rounded-none border-0 font-mono text-xs"
                                value={item.usage_rate || ''}
                                onChange={(e) => updateUsageDetail(index, 'usage_rate', e.target.value)}
                                disabled={!editMode}
                              />
                            </td>
                            <td className="border border-border p-1">
                              <Input
                                className="rounded-none border-0 font-mono text-xs"
                                value={item.charges || ''}
                                onChange={(e) => updateUsageDetail(index, 'charges', e.target.value)}
                                onFocus={() => editMode && handleRowFocus(rowId, item.page_number)}
                                disabled={!editMode}
                              />
                            </td>
                            {editMode && (
                              <td className="border border-border p-1 text-center">
                                <div className="flex items-center justify-center gap-1">
                                  <Button
                                    size="sm"
                                    variant={isRowReviewed ? "default" : "outline"}
                                    className={`h-6 w-6 p-0 rounded-none ${isRowReviewed ? 'bg-green-600 hover:bg-green-700' : ''}`}
                                    onClick={() => toggleRowReviewed(rowId)}
                                    title={isRowReviewed ? 'Marked as reviewed' : 'Mark as reviewed'}
                                  >
                                    <Check className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-6 w-6 p-0"
                                    onClick={() => removeUsageDetail(index)}
                                    title="Delete row"
                                  >
                                    <X className="h-4 w-4 text-red-500" />
                                  </Button>
                                </div>
                              </td>
                            )}
                          </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      
                      {/* Usage Total Row */}
                      <div className="border border-border border-t-0 bg-muted/50 p-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">Total Usage Charges:</span>
                          <span className="text-lg font-bold font-mono">
                            ${billValidation.usageTotal.toFixed(2)}
                          </span>
                        </div>
                      </div>
                      
                      {/* Usage to New Charges Validation */}
                      <div className={`border border-border border-t-0 p-3 ${billValidation.lineItemsMatchNewCharges ? 'bg-green-50' : 'bg-yellow-50'}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {billValidation.lineItemsMatchNewCharges ? (
                              <Check className="h-4 w-4 text-green-600" />
                            ) : (
                              <AlertTriangle className="h-4 w-4 text-yellow-600" />
                            )}
                            <span className="text-sm font-medium">
                              Usage + Other Charges = New Charges
                            </span>
                          </div>
                          <div className="text-sm font-mono">
                            ${billValidation.usageTotal.toFixed(2)} + ${billValidation.otherChargesTotal.toFixed(2)} = ${billValidation.lineItemsSum.toFixed(2)}
                            {!billValidation.lineItemsMatchNewCharges && (
                              <span className="text-yellow-700 ml-2">
                                (Expected: ${billValidation.newCharges.toFixed(2)})
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Usage Cost Breakdown Accordion */}
                      {usageBreakdown.length > 0 && (
                        <UsageCostBreakdown 
                          usageBreakdown={usageBreakdown} 
                          otherCharges={otherCharges}
                          validationData={validationData}
                        />
                      )}
                    </div>
                  </>
                )}
              </TabsContent>

              {/* Other Charges Tab */}
              <TabsContent value="other" className="flex-1 overflow-auto p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold">Other Charges</h3>
                  {editMode && (
                    <Button size="sm" variant="outline" className="rounded-none" onClick={addOtherCharge}>
                      <Plus className="h-4 w-4 mr-1" /> Add Row
                    </Button>
                  )}
                </div>
                
                {otherCharges.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-12">No other charges</p>
                ) : (
                  <div className="overflow-x-auto border border-border">
                    <table className="border-collapse" style={{ minWidth: '800px' }}>
                      <thead className="bg-muted">
                        <tr>
                          <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '50px' }}>Page</th>
                          <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '250px' }}>Line Item Description</th>
                          <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '100px' }}>Usage</th>
                          <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '100px' }}>Rate</th>
                          <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '100px' }}>Charge</th>
                          {editMode && <th className="border border-border p-2 w-20 text-center text-xs font-medium">Actions</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {otherCharges.map((item, index) => {
                          const rowId = `other-${index}`;
                          const isRowActive = editingRowId === rowId;
                          const isRowReviewed = reviewedRows.has(rowId);
                          return (
                          <tr 
                            key={index}
                            className={`transition-colors ${isRowActive ? 'bg-blue-50 ring-2 ring-blue-400 ring-inset' : ''} ${isRowReviewed ? 'bg-green-50' : ''} ${!editMode && item.page_number ? 'cursor-pointer hover:bg-muted/50' : ''}`}
                            onClick={() => !editMode && item.page_number && navigateToPage(item.page_number, item.line_item_description)}
                            title={item.page_number ? `Click to go to page ${item.page_number}` : ''}
                          >
                            <td className="border border-border p-1 text-center">
                              {item.page_number && (
                                <Badge 
                                  variant="outline" 
                                  className={`rounded-none text-xs font-mono cursor-pointer hover:bg-muted ${isRowActive ? 'bg-blue-100' : ''}`}
                                  onClick={(e) => { e.stopPropagation(); navigateToPage(item.page_number, item.line_item_description); }}
                                >
                                  p.{item.page_number}
                                </Badge>
                              )}
                            </td>
                            <td className="border border-border p-1">
                              <Input
                                className="rounded-none border-0 font-mono text-xs"
                                value={item.line_item_description || ''}
                                onChange={(e) => updateOtherCharge(index, 'line_item_description', e.target.value)}
                                onFocus={() => editMode && handleRowFocus(rowId, item.page_number)}
                                disabled={!editMode}
                              />
                            </td>
                            <td className="border border-border p-1">
                              <Input
                                className="rounded-none border-0 font-mono text-xs"
                                value={item.usage || ''}
                                onChange={(e) => updateOtherCharge(index, 'usage', e.target.value)}
                                onFocus={() => editMode && handleRowFocus(rowId, item.page_number)}
                                disabled={!editMode}
                              />
                            </td>
                            <td className="border border-border p-1">
                              <Input
                                className="rounded-none border-0 font-mono text-xs"
                                value={item.rate || ''}
                                onChange={(e) => updateOtherCharge(index, 'rate', e.target.value)}
                                onFocus={() => editMode && handleRowFocus(rowId, item.page_number)}
                                disabled={!editMode}
                              />
                            </td>
                            <td className="border border-border p-1">
                              <Input
                                className="rounded-none border-0 font-mono text-xs"
                                value={item.charge || ''}
                                onChange={(e) => updateOtherCharge(index, 'charge', e.target.value)}
                                onFocus={() => editMode && handleRowFocus(rowId, item.page_number)}
                                disabled={!editMode}
                              />
                            </td>
                            {editMode && (
                              <td className="border border-border p-1 text-center">
                                <div className="flex items-center justify-center gap-1">
                                  <Button
                                    size="sm"
                                    variant={isRowReviewed ? "default" : "outline"}
                                    className={`h-6 w-6 p-0 rounded-none ${isRowReviewed ? 'bg-green-600 hover:bg-green-700' : ''}`}
                                    onClick={() => toggleRowReviewed(rowId)}
                                    title={isRowReviewed ? 'Marked as reviewed' : 'Mark as reviewed'}
                                  >
                                    <Check className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-6 w-6 p-0"
                                    onClick={() => removeOtherCharge(index)}
                                    title="Delete row"
                                  >
                                    <X className="h-4 w-4 text-red-500" />
                                  </Button>
                                </div>
                              </td>
                            )}
                          </tr>
                        );
                        })}
                      </tbody>
                    </table>
                    
                    {/* Other Charges Total Row */}
                    <div className="border border-border border-t-0 bg-muted/50 p-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Total Other Charges:</span>
                        <span className="text-lg font-bold font-mono">
                          ${billValidation.otherChargesTotal.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </TabsContent>

              {/* Validation Line Items Tab (Engine-2) */}
              {intelligentValidation.enabled && (
                <TabsContent value="validation" className="flex-1 overflow-auto p-6" data-testid="validation-tab-content">
                  {/* Overall Status Banner */}
                  <div className={`mb-4 p-4 rounded-none border-2 ${intelligentValidation.bothValid ? 'bg-green-50 border-green-500' : 'bg-yellow-50 border-yellow-500'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {intelligentValidation.bothValid ? (
                          <Check className="h-6 w-6 text-green-600" />
                        ) : (
                          <AlertTriangle className="h-6 w-6 text-yellow-600" />
                        )}
                        <div>
                          <h3 className="font-bold text-lg">
                            {intelligentValidation.bothValid ? 'All Validations Passed' : 'Validation Issues Found'}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            New Charges: {intelligentValidation.validationMatchesNewCharges ? '✓ Valid' : '✗ Mismatch'} | 
                            Total Due: {intelligentValidation.totalDueMatches ? '✓ Valid' : '✗ Mismatch'}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Badge 
                          variant={intelligentValidation.validationMatchesNewCharges ? "default" : "destructive"}
                          className={`rounded-none ${intelligentValidation.validationMatchesNewCharges ? 'bg-green-600' : ''}`}
                        >
                          New Charges: {intelligentValidation.validationMatchesNewCharges ? 'VALID' : 'MISMATCH'}
                        </Badge>
                        <Badge 
                          variant={intelligentValidation.totalDueMatches ? "default" : "destructive"}
                          className={`rounded-none ${intelligentValidation.totalDueMatches ? 'bg-green-600' : ''}`}
                        >
                          Total Due: {intelligentValidation.totalDueMatches ? 'VALID' : 'MISMATCH'}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-bold">Validation Line Items</h3>
                      <p className="text-sm text-muted-foreground">Line items that add up to New Charges</p>
                    </div>
                  </div>

                  {intelligentValidation.validationLineItems.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-12">No validation line items found</p>
                  ) : (
                    <div className="overflow-x-auto border border-border">
                      <table className="w-full border-collapse" style={{ minWidth: '600px' }}>
                        <thead className="bg-muted">
                          <tr>
                            <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '50px' }}>Page</th>
                            <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '300px' }}>Line Item Description</th>
                            <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '80px' }}>Source</th>
                            <th className="border border-border p-2 text-right text-xs font-medium whitespace-nowrap" style={{ minWidth: '100px' }}>Charge</th>
                          </tr>
                        </thead>
                        <tbody>
                          {intelligentValidation.validationLineItems.map((item, index) => (
                            <tr 
                              key={index}
                              className={`transition-colors ${item.page_number ? 'cursor-pointer hover:bg-muted/50' : ''}`}
                              onClick={() => item.page_number && navigateToPage(item.page_number, item.description)}
                              title={item.page_number ? `Click to go to page ${item.page_number}` : ''}
                            >
                              <td className="border border-border p-2 text-center">
                                {item.page_number ? (
                                  <Badge variant="outline" className="rounded-none text-xs font-mono">
                                    p.{item.page_number}
                                  </Badge>
                                ) : (
                                  <span className="text-xs text-muted-foreground">-</span>
                                )}
                              </td>
                              <td className="border border-border p-2 text-sm">{item.description}</td>
                              <td className="border border-border p-2">
                                <Badge variant="secondary" className="rounded-none text-xs">
                                  {item.source}
                                </Badge>
                              </td>
                              <td className="border border-border p-2 text-right font-mono text-sm">
                                ${item.charge.toFixed(2)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>

                      {/* Validation Summary */}
                      <div className="border border-border border-t-0 bg-muted/50 p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">Line Items Total:</span>
                          <span className="text-lg font-bold font-mono">${intelligentValidation.validationTotal.toFixed(2)}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">New Charges (Statement):</span>
                          <span className="text-lg font-bold font-mono">${intelligentValidation.newCharges.toFixed(2)}</span>
                        </div>
                        {!intelligentValidation.validationMatchesNewCharges && (
                          <div className={`flex items-center justify-between p-2 ${intelligentValidation.newChargesDifference > 0 ? 'bg-red-50 text-red-700' : 'bg-yellow-50 text-yellow-700'}`}>
                            <span className="text-sm font-medium">Difference:</span>
                            <span className="font-bold font-mono">
                              {intelligentValidation.newChargesDifference > 0 ? '+' : ''}${intelligentValidation.newChargesDifference.toFixed(2)}
                            </span>
                          </div>
                        )}
                        {intelligentValidation.validationMatchesNewCharges && (
                          <div className="flex items-center gap-2 p-2 bg-green-50 text-green-700">
                            <Check className="h-5 w-5" />
                            <span className="font-medium">New Charges validated successfully!</span>
                          </div>
                        )}
                      </div>

                      {/* Total Amount Due Validation */}
                      <div className="border border-border border-t-0 p-4 space-y-2">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-bold">Total Amount Due Validation</h4>
                          {intelligentValidation.totalDueMatches && (
                            <Badge variant="outline" className="rounded-none text-xs">
                              Method: {intelligentValidation.totalDueValidationMethod === 'standard' ? 'Standard' :
                                       intelligentValidation.totalDueValidationMethod === 'direct_match' ? 'Direct Match' :
                                       intelligentValidation.totalDueValidationMethod === 'line_items_plus_balance' ? 'Line Items + BF' :
                                       intelligentValidation.totalDueValidationMethod === 'new_charges_plus_bf' ? 'NC + BF' : 'N/A'}
                            </Badge>
                          )}
                        </div>
                        
                        {/* Show breakdown based on which method matched */}
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <span className="text-muted-foreground">New Charges:</span>
                          <span className="font-mono text-right">${intelligentValidation.newCharges.toFixed(2)}</span>
                          
                          {intelligentValidation.breakdown.balanceForward !== 0 && (
                            <>
                              <span className="text-muted-foreground">+ Balance Forward:</span>
                              <span className="font-mono text-right">${intelligentValidation.breakdown.balanceForward.toFixed(2)}</span>
                            </>
                          )}
                          
                          {intelligentValidation.breakdown.lateFee > 0 && !intelligentValidation.breakdown.lateFeeInOtherCharges && (
                            <>
                              <span className="text-muted-foreground">+ Late Fee:</span>
                              <span className="font-mono text-right">${intelligentValidation.breakdown.lateFee.toFixed(2)}</span>
                            </>
                          )}
                          
                          {intelligentValidation.breakdown.lateFee > 0 && intelligentValidation.breakdown.lateFeeInOtherCharges && (
                            <>
                              <span className="text-muted-foreground text-xs italic">Late Fee (in Other Charges):</span>
                              <span className="font-mono text-right text-xs italic">${intelligentValidation.breakdown.lateFee.toFixed(2)}</span>
                            </>
                          )}
                          
                          {intelligentValidation.breakdown.credits > 0 && (
                            <>
                              <span className="text-muted-foreground">- Credits:</span>
                              <span className="font-mono text-right">${intelligentValidation.breakdown.credits.toFixed(2)}</span>
                            </>
                          )}
                          
                          {intelligentValidation.breakdown.adjustments !== 0 && (
                            <>
                              <span className="text-muted-foreground">+ Adjustments:</span>
                              <span className="font-mono text-right">${intelligentValidation.breakdown.adjustments.toFixed(2)}</span>
                            </>
                          )}
                          
                          <span className="font-medium border-t pt-1">= Calculated:</span>
                          <span className="font-mono font-medium text-right border-t pt-1">
                            ${intelligentValidation.calculatedTotalDue.toFixed(2)}
                          </span>
                          <span className="font-medium">Statement Total Due:</span>
                          <span className="font-mono font-medium text-right">${intelligentValidation.totalAmountDue.toFixed(2)}</span>
                        </div>
                        
                        {intelligentValidation.totalDueMatches ? (
                          <div className="flex items-center gap-2 p-2 bg-green-50 text-green-700 mt-2">
                            <Check className="h-5 w-5" />
                            <span className="font-medium">Total Amount Due validated!</span>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between p-2 bg-yellow-50 text-yellow-700 mt-2">
                            <span className="text-sm font-medium">Difference:</span>
                            <span className="font-bold font-mono">
                              {intelligentValidation.totalDueDifference > 0 ? '+' : ''}${intelligentValidation.totalDueDifference.toFixed(2)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </TabsContent>
              )}
            </Tabs>
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>

      {/* Audit Trail Modal */}
      <Dialog open={showAuditModal} onOpenChange={setShowAuditModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Document History
            </DialogTitle>
            <DialogDescription>
              Complete audit trail for this document
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-auto">
            {auditLogs.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No history available</p>
            ) : (
              <div className="space-y-3">
                {auditLogs.map((log, index) => {
                  const logDate = log.created_at || log.timestamp;
                  const formattedDate = logDate ? format(new Date(logDate), 'MMM d, yyyy h:mm a') : 'Unknown date';
                  return (
                    <div key={log.id || index} className="border border-border rounded-none p-3 bg-card">
                      <div className="flex items-center justify-between mb-2">
                        <Badge variant="outline" className="rounded-none text-xs font-mono">
                          {log.action?.replace(/_/g, ' ')}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {formattedDate}
                        </span>
                      </div>
                      <p className="text-sm">
                        <span className="font-medium">{log.user_name}</span>
                        {log.action === 'status_changed' && log.changes && (
                          <span> changed status from <Badge variant="secondary" className="rounded-none text-xs mx-1">{log.changes.from}</Badge> to <Badge variant="secondary" className="rounded-none text-xs mx-1">{log.changes.to}</Badge></span>
                        )}
                        {log.action === 'assignment_changed' && log.changes && (
                          <span> {log.changes.assigned_to ? `assigned document to ${log.changes.assigned_to_name || 'a user'}` : 'removed assignment'}</span>
                        )}
                        {log.action === 'data_updated' && (
                          <span> updated extracted data</span>
                        )}
                        {log.action === 'reprocessed' && (
                          <span> triggered document re-parsing</span>
                        )}
                        {log.action === 'uploaded' && (
                          <span> uploaded this document</span>
                        )}
                      </p>
                      {log.changes && typeof log.changes === 'object' && !['from', 'to', 'assigned_to'].some(k => k in log.changes) && (
                        <details className="mt-2">
                          <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                            View changes ({Object.keys(log.changes).length} fields)
                          </summary>
                          <div className="mt-2 text-xs font-mono bg-muted p-2 rounded-none max-h-32 overflow-auto">
                            {Object.entries(log.changes).map(([field, values]) => (
                              <div key={field} className="mb-1">
                                <span className="text-muted-foreground">{field}:</span>{' '}
                                {typeof values === 'object' && values !== null ? (
                                  <><span className="line-through text-red-600">{String(values.old || 'empty')}</span>{' → '}<span className="text-green-600">{String(values.new || 'empty')}</span></>
                                ) : (
                                  <span className="text-green-600">{String(values)}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Viewer;
